/**
 * WHICH COLUMNS an entity shows when nobody has said — one derivation, so a
 * child grid and the full list agree about what an Organization looks like.
 *
 * Christopher's decision, 2026-08-06, on the child results inside a relation
 * band: "the child grid shows the child entity's OWN default view, implicitly
 * filtered by the relationship". That sentence only holds if "its own default
 * view" means the same thing in both places — a second, private answer in the
 * band is how the parent list and the child grid start disagreeing about the
 * same entity, and the disagreement is invisible until someone edits a view and
 * only one surface moves.
 *
 * So this consumes `ListView`'s canonical server answer:
 *
 *   `core.resolve_experience(view)` → effective document + provenance
 *   nothing registered      → SYNTHESIZE from `field_meta`: the `is_summary`
 *                             columns, else the first few
 *
 * `ready` is not `!isLoading`. An entity with no registered view legitimately
 * resolves to `[]`, so "settled" is `data != null` — and synthesizing before
 * then invents a columns list that the real default view then replaces, which
 * on the list cost a whole thrown-away server page (#385) and here costs a
 * visible column reshuffle under the reader's eyes.
 */
import { useMemo } from 'react'
import type { SemanticField } from '@solum/sdk'
import { useSemantics } from '../data/queries'
import { useRegistryExperience } from '../experience/governed'

export interface DefaultColumns {
  /** column names, in view order */
  columns: string[]
  /** the semantic field behind each column — labels, kinds, options */
  fields: Map<string, SemanticField>
  /** every source has settled; before this the columns are provisional */
  ready: boolean
}

/** The `is_summary` columns, else the first few — `ListView`'s synth rule. */
export function synthesizeColumns(fields: Pick<SemanticField, 'column_name' | 'is_summary'>[], max = 6): string[] {
  const summary = fields.filter((f) => f.is_summary).map((f) => f.column_name)
  return (summary.length ? summary : fields.slice(0, max).map((f) => f.column_name)).slice(0, max)
}

export function useDefaultColumns(entity: string, max = 6): DefaultColumns {
  const view = useRegistryExperience(entity, 'view')
  const sem = useSemantics(entity)

  const fields = useMemo(
    () => new Map(((sem.data?.fields ?? []) as SemanticField[]).map((f) => [f.column_name, f])),
    [sem.data])

  const columns = useMemo(() => {
    if (view.data?.state === 'resolved') {
      const document = view.data.effective?.document as { columns?: unknown } | undefined
      const cols = (Array.isArray(document?.columns)
        ? document.columns.filter((column): column is string => typeof column === 'string')
        : []).slice(0, max)
      // A registered view with an empty columns list is a configuration the
      // reader cannot act on — fall through rather than render a grid with no
      // columns in it.
      if (cols.length) return cols
    }
    return synthesizeColumns((sem.data?.fields ?? []) as SemanticField[], max)
  }, [view.data, sem.data, max])

  return { columns, fields, ready: view.data != null && sem.data != null }
}
